import { useAtomValue } from 'jotai';
import { CtaButtonType, CtaForm } from '@pairy/sheard/types';

import CtaFormLayer from '../../BulkViewer/Desktop/CtaFormLayer';
import CtaButtonLayer from '../..//BulkViewer/Desktop/CtaButtonLayer';
import { BulkFileType, UserFileInfoType } from '../../../types/file';
import {
  currentPageAtom,
  zoomLevelAtom,
  isCtaModalOpenAtom,
  thumbnailsAtom,
  isLanguageDropdownOpenAtom,
} from '../../../store/index.atom';
import { MessageBoxItemType } from '../../PdfViewer/index.types';
import DesktopIntro from '../../@common/Intro/Desktop';
import DesktopPageWrapper from '../../@common/PageWrapper/Desktop';
import DesktopPage from '../../@common/Viewer/Desktop';
import DesktopFreeBadge from '../../@common/FreeBadge/Desktop';
import { isPreviewFormSubmittedAtom } from '../../BulkPdfPreview/index.atom';
import DesktopPageNavigationButton from '../../@common/PageNavigationButton/Desktop';
import DesktopViewerLayer from '../../@common/Layer/DefaultViewerLayer/Desktop';
import ZoomableViewer from '../../@common/ZoomableViewer';

import AnnotationLayer from './AnnotationLayer';

interface Props {
  file: BulkFileType;
  user: UserFileInfoType;
  ctaButtonList?: CtaButtonType[];
  ctaForm?: CtaForm;
  messageBoxItems?: MessageBoxItemType[];
  translateKoToEn: (item: string) => string;
  updateNextThumbnails: (page: number) => void;
  updatePrevThumbnails: (page: number) => void;
  blindPages: number[] | null;
  isPreview?: boolean;
}

const BulkDesktopViewer = ({
  file,
  user,
  ctaButtonList,
  ctaForm,
  messageBoxItems,
  translateKoToEn,
  updateNextThumbnails,
  updatePrevThumbnails,
  blindPages,
  isPreview = false,
}: Props) => {
  const currentPage = useAtomValue(currentPageAtom);
  const isCtaModalOpen = useAtomValue(isCtaModalOpenAtom);
  const zoomLevel = useAtomValue(zoomLevelAtom);
  const isPreviewFormSubmitted = useAtomValue(isPreviewFormSubmittedAtom);
  const thumbnails = useAtomValue(thumbnailsAtom);
  const isLanguageDropdownOpen = useAtomValue(isLanguageDropdownOpenAtom);

  const { domain, plan } = user;
  const {
    name,
    isDownloadEnabled,
    isEmailVerificationEnabled,
    isFormSubmitted,
    pages,
  } = file;

  const ctaFormPage = ctaForm?.page;
  const blindMode = ctaForm?.blind;

  const isNextPageButtonDisabled =
    currentPage >= Number(ctaFormPage) &&
    blindMode === 'startFrom' &&
    (isPreview ? !isPreviewFormSubmitted : !isFormSubmitted);

  const isRedirectCtaFormAppropriate = Boolean(
    !(blindMode === 'none') && !isFormSubmitted,
  );

  /*첫 번째 페이지에 폼이 설정되어 있는 경우에 폼 제출 이후에 보여지도록 한다 */
  const isIntroVisible =
    ctaFormPage === 1 && blindMode === 'startFrom' ? isFormSubmitted : true;

  return (
    <>
      {isIntroVisible && <DesktopIntro translateKoToEn={translateKoToEn} />}
      <DesktopPageWrapper
        totalPage={Number(pages)}
        updateNextThumbnails={updateNextThumbnails}
        updatePrevThumbnails={updatePrevThumbnails}
        isWheelDisabled={
          isNextPageButtonDisabled ||
          isCtaModalOpen ||
          zoomLevel !== 1 ||
          isLanguageDropdownOpen
        }
      >
        <DesktopViewerLayer
          fileName={name}
          domain={domain}
          isDownloadEnabled={isPreview ? false : isDownloadEnabled}
          ctaFormPage={ctaFormPage}
          translateKoToEn={translateKoToEn}
          isRedirectCtaFormAppropriate={isRedirectCtaFormAppropriate}
          isShareable={!isPreview}
          isBulk
        />

        <ZoomableViewer>
          <DesktopPage
            thumnails={thumbnails?.map((value) => {
              if (value?.thumbnail) {
                return {
                  src: value.thumbnail,
                  imageSources: value.imageSources,
                };
              }
              return null;
            })}
            blindPages={blindPages}
          />

          {thumbnails && (
            <AnnotationLayer
              thumbnails={thumbnails}
              messageBoxItems={messageBoxItems}
              ctaForm={ctaForm}
              isPreview={isPreview}
              isFormSubmitted={isFormSubmitted}
            />
          )}
        </ZoomableViewer>

        <CtaFormLayer
          ctaForm={ctaForm}
          isPreview={isPreview}
          isEmailVerificationEnabled={isEmailVerificationEnabled}
          isFormSubmitted={isFormSubmitted}
          translateKoToEn={translateKoToEn}
          userSubscriptionPlan={plan}
        />
        <CtaButtonLayer ctaButtonList={ctaButtonList} isPreview={isPreview} />

        <DesktopPageNavigationButton
          totalPage={Number(pages)}
          updatePrevThumbnails={updatePrevThumbnails}
          isNextPageButtonDisabled={isNextPageButtonDisabled}
          updateNextThumbnails={updateNextThumbnails}
          ctaFormPage={ctaFormPage}
          isMoveCtaFormPageRequirements={
            blindMode === 'startFrom' && !isFormSubmitted
          }
        />

        <DesktopFreeBadge userSubscriptionPlan={plan} />
      </DesktopPageWrapper>
    </>
  );
};

export default BulkDesktopViewer;
